export type ChatRole = "user" | "assistant";

export type ChatMessage = {
  id: string;
  role: ChatRole;
  content: string;
  createdAt: number;
  kind?:
    | "text"
    | "contract"
    | "recommendation"
    | "recommendation_loading"
    | "recommendation_error"
    | "recommendation_empty"
    | "recommendation_preview"
    | "midchat_feedback";
  match?: TopMatch | null;
  preview?: PreviewMatch | null;
};

export type PreviewMatch = {
  therapist: {
    id: string;
    firstName: string | null;
    avatarUrl: string | null;
    specializations: string[] | null;
  };
  matchScore: number;
};

export type EndedReason = "timer" | "message_cap" | "manual";

export type TherapistPreview = {
  id: string;
  /** Public profile slug; older backends omit it, so links fall back to the id. */
  slug?: string | null;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
  specializations: string[] | null;
  yearsOfExperience: string | null;
  /** "Ore de practică" band floor; null = nothing to show. */
  practiceHours?: number | null;
  sessionPriceIndividual: number | null;
  sessionPriceCouple: number | null;
  sessionTypes: string[] | null;
  availabilitySlots: { startTime: string; endTime: string }[] | null;
  /** Therapist opted in to free 20min discovery sessions. Default true server-side. */
  acceptsDiscoverySessions?: boolean;
};

export type TherapistMatch = {
  therapist: TherapistPreview;
  matchScore: number;
};

export type TopMatch = TherapistMatch & {
  rationale: string;
  reasons: string[];
};

export type FinalizeResult = {
  dimensions: Record<string, number>;
  topMatch: TopMatch | null;
  alternates: TherapistMatch[];
};

export const SESSION_SCHEMA_VERSION = 5;

export type SessionState = {
  v: number;
  sessionId: string;
  name: string;
  startedAt: number;
  /** Total ms spent paused (frozen out of elapsed). */
  pausedTotalMs?: number;
  /** When currently paused, timestamp when pause began. */
  pausedAt?: number;
  messages: ChatMessage[];
  endedReason?: EndedReason;
  match?: FinalizeResult;
  /** When user clicked "Mai am o întrebare" after the recommendation. */
  extraTurnsGranted?: number;
  /** Track first-user-message time-to-send analytics. */
  firstUserMsgAt?: number;
  /** Set when the hero-on-home initiated the session — chat-shell auto-streams on mount. */
  pendingFirstSend?: boolean;
  /** True once the mid-chat feedback bubble has been shown — never show twice. */
  midchatFeedbackShown?: boolean;
  /** True once the abandonment survey has been shown — never show twice. */
  abandonSurveyShown?: boolean;
};

export const SESSION_DURATION_MS = 20 * 60 * 1000;
export const MAX_USER_MESSAGES = 25;
export const EXTRA_TURNS_AFTER_RECOMMENDATION = 3;
export const MAX_MESSAGE_LENGTH = 1000;
export const SESSION_STORAGE_KEY = "sesiune_ai";
export const SESSION_STALE_AFTER_MS = 30 * 24 * 60 * 60 * 1000;
